import { useMemo, useEffect } from "react";
import { useForm } from "@inertiajs/react";
import {
  Box,
  Container,
  Heading,
  Button,
  Text,
  VStack,
  Input,
  HStack,
  Select,
  createListCollection,
  Field,
  Fieldset,
  Card,
  Stack,
  Span,
} from "@chakra-ui/react";
import { prefectures, genders, playerLevels, ageGroups, calculateAgeGroup, calculateInitialRating } from "@/lib/utils";
import { player_registration_path } from "@/routes";

export default function PlayerNew() {
  const { data, setData, post, processing, errors } = useForm({
    display_name: "",
    prefecture: "",
    birth_year: "",
    birth_month: "",
    birth_day: "",
    gender: "",
    player_level: "",
  });

  const playerLevelDescriptions: Record<string, string> = {
    newbie: "バスケ歴1年未満",
    beginner: "バスケ歴1～3年",
    intermediate: "バスケ歴4～6年",
    advanced: "バスケ歴7年以上",
    semi_pro: "過去3年以内",
    pro: "過去3年以内",
  };

  const ageGroup = useMemo(() => {
    return calculateAgeGroup(parseInt(data.birth_year) || 0, parseInt(data.birth_month) || 0, parseInt(data.birth_day) || 0);
  }, [data.birth_year, data.birth_month, data.birth_day]);

  const rating = useMemo(() => {
    return calculateInitialRating(ageGroup, data.gender, data.player_level);
  }, [ageGroup, data.gender, data.player_level]);

  // 年齢カテゴリーがU12/U15/U18でプロが選択されている場合、自動的にリセット
  useEffect(() => {
    if (ageGroup && [ageGroups["under_12"], ageGroups["under_15"], ageGroups["under_18"]].includes(ageGroup) && data.player_level === "pro") {
      setData("player_level", "");
    }
  }, [ageGroup, data.player_level]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post(player_registration_path());
  };

  const prefecturesList = createListCollection({
    items: Object.entries(prefectures).map(([key, value]) => ({ label: value, value: key })),
  });

  const playerLevelsList = useMemo(
    () =>
      createListCollection({
        items: Object.entries(playerLevels)
          .filter(([key]) => {
            // U12, U15, U18の場合はプロレベルを除外
            return !(ageGroup && [ageGroups["under_12"], ageGroups["under_15"], ageGroups["under_18"]].includes(ageGroup) && key === "pro");
          })
          .map(([key, value]) => ({ label: value, value: key })),
      }),
    [ageGroup]
  );

  const yearsList = createListCollection({
    items: Array.from({ length: 100 }, (_, i) => {
      const year = new Date().getFullYear() - i;
      return { label: year, value: year.toString() };
    }),
  });

  // 月の選択肢
  const monthsList = createListCollection({
    items: Array.from({ length: 12 }, (_, i) => {
      const month = i + 1;
      return { label: month, value: month.toString() };
    }),
  });

  // 日の選択肢
  const daysList = createListCollection({
    items: Array.from({ length: 31 }, (_, i) => {
      const day = i + 1;
      return { label: day, value: day.toString() };
    }),
  });

  return (
    <Container maxW="2xl" py={8}>
      <VStack gap={8} align="stretch">
        {/* Header */}
        <Box>
          <Heading size="2xl">プレイヤー情報 登録</Heading>
          <Text color="gray.600" mt={2}>
            あなたのプレイヤー情報を入力してください。
            <br />
            入力内容に基づいて初期レーティングが計算されます。
          </Text>
        </Box>

        {/* Main Form Card */}
        <Card.Root bg="white" shadow="lg" borderRadius="xl">
          <Card.Body p={8}>
            <Box as="form" onSubmit={handleSubmit}>
              <Stack gap={6}>
                {/* Display Name */}
                <Field.Root invalid={!!errors.display_name}>
                  <Field.Label fontWeight="semibold" fontSize="sm">
                    表示名
                  </Field.Label>
                  <Field.HelperText color="gray.500" fontSize="xs" mb={2}>
                    他のプレイヤーに表示される名前です
                  </Field.HelperText>
                  <Input
                    value={data.display_name}
                    onChange={e => setData("display_name", e.target.value)}
                    placeholder="例: 宮城 リョータ"
                    autoComplete="username"
                    borderRadius="lg"
                    bg="gray.50"
                    _focus={{ bg: "white", borderColor: "blue.400", shadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
                  />
                  <Field.ErrorText>{errors.display_name}</Field.ErrorText>
                </Field.Root>

                {/* Prefecture */}
                <Field.Root invalid={!!errors.prefecture}>
                  <Field.Label fontWeight="semibold" fontSize="sm">
                    都道府県
                  </Field.Label>
                  <Select.Root collection={prefecturesList} value={[data.prefecture]} onValueChange={value => setData("prefecture", value.value[0])} size="lg">
                    <Select.Control>
                      <Select.Trigger
                        borderRadius="lg"
                        bg="gray.50"
                        _focus={{ bg: "white", borderColor: "blue.400", shadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
                      >
                        <Select.ValueText placeholder="都道府県を選択" />
                      </Select.Trigger>
                      <Select.IndicatorGroup>
                        <Select.Indicator />
                        <Select.ClearTrigger />
                      </Select.IndicatorGroup>
                    </Select.Control>

                    <Select.Positioner>
                      <Select.Content borderRadius="lg" shadow="xl">
                        {prefecturesList.items.map(prefecture => (
                          <Select.Item item={prefecture} key={prefecture.value}>
                            {prefecture.label}
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                  <Field.ErrorText>{errors.prefecture}</Field.ErrorText>
                </Field.Root>

                {/* Gender */}
                <Field.Root invalid={!!errors.gender}>
                  <Field.Label fontWeight="semibold" fontSize="sm">
                    性別
                  </Field.Label>
                  <HStack gap={3} mt={2}>
                    {Object.entries(genders).map(([key, value]) => (
                      <Button
                        key={key}
                        onClick={() => setData("gender", key)}
                        variant={data.gender === key ? "surface" : "outline"}
                        colorPalette={data.gender === key ? "blue" : "gray"}
                        size="lg"
                        borderRadius="lg"
                        _hover={{
                          transform: "translateY(-1px)",
                          shadow: "md",
                        }}
                        transition="all 0.2s"
                      >
                        {value}
                      </Button>
                    ))}
                  </HStack>
                  <Field.ErrorText>{errors.gender}</Field.ErrorText>
                </Field.Root>

                {/* Birthday */}
                <Fieldset.Root invalid={!!errors.birth_year || !!errors.birth_month || !!errors.birth_day}>
                  <Fieldset.Legend fontWeight="semibold" fontSize="sm">
                    生年月日
                  </Fieldset.Legend>
                  <Text color="gray.500" fontSize="xs">
                    年別カテゴリーを計算するために利用されます。他の人に生年月日は公開されません。
                  </Text>
                  <Fieldset.Content>
                    <HStack gap={2}>
                      {/* Year */}
                      <HStack gap={2}>
                        <Field.Root invalid={!!errors.birth_year}>
                          <Select.Root
                            collection={yearsList}
                            value={data.birth_year ? [data.birth_year] : []}
                            onValueChange={value => setData("birth_year", value.value[0] || "")}
                            size="md"
                            width={"5rem"}
                          >
                            <Select.Control>
                              <Select.Trigger
                                borderRadius="lg"
                                bg="gray.50"
                                _focus={{ bg: "white", borderColor: "blue.400", shadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
                              >
                                <Select.ValueText />
                              </Select.Trigger>
                              <Select.IndicatorGroup>
                                <Select.Indicator />
                              </Select.IndicatorGroup>
                            </Select.Control>
                            <Select.Positioner>
                              <Select.Content borderRadius="lg" shadow="xl" maxH="200px">
                                {yearsList.items.map(year => (
                                  <Select.Item key={year.value} item={year}>
                                    {year.label}
                                    <Select.ItemIndicator />
                                  </Select.Item>
                                ))}
                              </Select.Content>
                            </Select.Positioner>
                          </Select.Root>
                        </Field.Root>
                        <Text fontSize="sm" color="gray.700">
                          年
                        </Text>
                      </HStack>

                      {/* Month */}
                      <HStack gap={2}>
                        <Field.Root invalid={!!errors.birth_month}>
                          <Select.Root
                            collection={monthsList}
                            value={data.birth_month ? [data.birth_month] : []}
                            onValueChange={value => setData("birth_month", value.value[0] || "")}
                            size="md"
                            width={"4rem"}
                          >
                            <Select.Control>
                              <Select.Trigger
                                borderRadius="lg"
                                bg="gray.50"
                                _focus={{ bg: "white", borderColor: "blue.400", shadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
                              >
                                <Select.ValueText />
                              </Select.Trigger>
                              <Select.IndicatorGroup>
                                <Select.Indicator />
                              </Select.IndicatorGroup>
                            </Select.Control>
                            <Select.Positioner>
                              <Select.Content borderRadius="lg" shadow="xl">
                                {monthsList.items.map(month => (
                                  <Select.Item key={month.value} item={month}>
                                    {month.label}
                                    <Select.ItemIndicator />
                                  </Select.Item>
                                ))}
                              </Select.Content>
                            </Select.Positioner>
                          </Select.Root>
                        </Field.Root>
                        <Text fontSize="sm" color="gray.700">
                          月
                        </Text>
                      </HStack>

                      {/* Day */}
                      <HStack gap={2}>
                        <Field.Root invalid={!!errors.birth_day}>
                          <Select.Root
                            collection={daysList}
                            value={data.birth_day ? [data.birth_day] : []}
                            onValueChange={value => setData("birth_day", value.value[0] || "")}
                            size="md"
                            width={"4rem"}
                          >
                            <Select.Control>
                              <Select.Trigger
                                borderRadius="lg"
                                bg="gray.50"
                                _focus={{ bg: "white", borderColor: "blue.400", shadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
                              >
                                <Select.ValueText />
                              </Select.Trigger>
                              <Select.IndicatorGroup>
                                <Select.Indicator />
                              </Select.IndicatorGroup>
                            </Select.Control>
                            <Select.Positioner>
                              <Select.Content borderRadius="lg" shadow="xl">
                                {daysList.items.map(day => (
                                  <Select.Item key={day.value} item={day}>
                                    {day.label}
                                    <Select.ItemIndicator />
                                  </Select.Item>
                                ))}
                              </Select.Content>
                            </Select.Positioner>
                          </Select.Root>
                        </Field.Root>
                        <Text fontSize="sm" color="gray.700">
                          日
                        </Text>
                      </HStack>
                    </HStack>
                  </Fieldset.Content>
                  <Fieldset.ErrorText>{errors.birth_day}</Fieldset.ErrorText>
                </Fieldset.Root>

                {/* Player Level */}
                <Field.Root invalid={!!errors.player_level}>
                  <Field.Label fontWeight="semibold" fontSize="sm">
                    プレイヤーレベル
                  </Field.Label>
                  <Field.HelperText color="gray.500" fontSize="xs" mb={2}>
                    あなたの現在のスキルレベルを選択してください
                  </Field.HelperText>
                  <Select.Root
                    collection={playerLevelsList}
                    value={[data.player_level]}
                    onValueChange={value => setData("player_level", value.value[0])}
                    size="lg"
                  >
                    <Select.Control>
                      <Select.Trigger
                        borderRadius="lg"
                        bg="gray.50"
                        _focus={{ bg: "white", borderColor: "blue.400", shadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
                      >
                        <Select.ValueText placeholder="レベルを選択" />
                      </Select.Trigger>
                      <Select.IndicatorGroup>
                        <Select.Indicator />
                        <Select.ClearTrigger />
                      </Select.IndicatorGroup>
                    </Select.Control>

                    <Select.Positioner>
                      <Select.Content borderRadius="lg" shadow="xl">
                        {playerLevelsList.items.map(playerLevel => (
                          <Select.Item item={playerLevel} key={playerLevel.value}>
                            <Stack gap="0.5">
                              <Select.ItemText>{playerLevel.label}</Select.ItemText>
                              <Span color="fg.muted" textStyle="xs">
                                {playerLevelDescriptions[playerLevel.value]}
                              </Span>
                            </Stack>
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                  <Field.ErrorText>{errors.player_level}</Field.ErrorText>
                </Field.Root>

                {/* Rating and Age Group Display */}
                <Box bg="gradient-to-r from-blue.50 to-purple.50" p={6} borderRadius="xl" border="1px solid" borderColor="blue.100">
                  <VStack gap={4} align="stretch">
                    {/* Age Group */}

                    <HStack justify="space-between" align="center">
                      <Box>
                        <Text fontSize="sm" color="gray.600" fontWeight="medium">
                          年齢カテゴリー
                        </Text>
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          生年月日から自動計算されます
                        </Text>
                      </Box>
                      {ageGroup && (
                        <Box bg="green.100" fontSize="md" px={3} py={1} borderRadius="full" fontWeight="medium">
                          {ageGroup}
                        </Box>
                      )}
                    </HStack>

                    {/* Initial Rating */}
                    <HStack justify="space-between" align="center">
                      <Box>
                        <Text fontSize="sm" color="gray.600" fontWeight="medium">
                          初期レーティング
                        </Text>
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          年齢カテゴリー・性別・プレイヤーレベルに基づいて計算されます
                        </Text>
                      </Box>
                      {rating && (
                        <Box bg="blue.100" fontSize="lg" px={4} py={2} borderRadius="full" fontWeight="medium">
                          {rating}
                        </Box>
                      )}
                    </HStack>
                  </VStack>
                </Box>

                {/* Submit Button */}
                <Button
                  type="submit"
                  colorPalette="blue"
                  size="xl"
                  borderRadius="xl"
                  loading={processing}
                  loadingText="登録中..."
                  _hover={{
                    transform: "translateY(-2px)",
                    shadow: "xl",
                  }}
                  transition="all 0.2s"
                  mt={4}
                >
                  登録する
                </Button>
              </Stack>
            </Box>
          </Card.Body>
        </Card.Root>
      </VStack>
    </Container>
  );
}
